import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import ProjectMember, { ProjectMemberJSON } from '../models/projectMember';
import { ProjectMemberRoleType } from '../models/projectRole';
import useProjectMembersSystem from '../system/useProjectMembersSystem';
import { QueryKeys } from './keysAndFilters';

export type UseUpdateProjectMemberRoleMutationParams = {
	projectID: number;
	onSuccess?: ( member: ProjectMember, role: ProjectMemberRoleType ) => void,
	onError?: ( error: unknown ) => void
};

const useUpdateProjectMemberRoleMutation = ( {
	projectID, onSuccess: onMutationSuccess, onError: onMutationError
}: UseUpdateProjectMemberRoleMutationParams ) => {
	const system = useProjectMembersSystem();
	const queryClient = useQueryClient();

	const onSuccess = useCallback( (
		_data: ProjectMemberJSON,
		{ member, role }: { member: ProjectMember, role: ProjectMemberRoleType }
	) => {
		queryClient.invalidateQueries( {
			queryKey: QueryKeys.forProjectMembers( { projectID } ),
			refetchType: 'active'
		} );

		onMutationSuccess?.( member, role );
	}, [ onMutationSuccess, projectID, queryClient ] );

	return useMutation(
		( { member, role }: { member: ProjectMember, role: ProjectMemberRoleType } ) => (
			system.updateProjectMemberRole( { projectID, memberID: member.id, role } )
		),
		{ onSuccess, onError: onMutationError }
	);
}

export default useUpdateProjectMemberRoleMutation;
